/* =====================================================
   Damlab — Homepage Modules
   ----------------------------------------------------
   Each behavior from the original main.js is now a
   discrete App module:
     - home.loader         (loading screen + hero reveal)
     - home.navbar         (sticky show/hide on hero exit)
     - home.mobileNav      (hamburger / fullscreen nav)
     - home.tabs           (popular-products tabs)
     - home.popularCart    (popular-products "Toevoegen" → cart)
     - home.faq            (accordion)
     - home.hscroll        (desktop scroll-hijack + mobile dots)
     - home.smoothAnchors  (in-page anchor smooth scroll)
     - home.marquee        (Instagram strip)
     - home.installVideo   (custom player A)
     - home.waterVideo     (custom player B)
     - home.lang           (language dropdown)
     - home.wfBars         (waterfilter % bars)

   All listeners use ctx.on() so unmount tears them
   down via AbortController — no leaks, no duplicates.
   ===================================================== */
(function (App) {
  'use strict';
  if (!App) { console.error('[home] App core not loaded'); return; }

  // ── home.loader ─────────────────────────────────────
  App.register('home.loader', {
    mount(ctx) {
      const loader = document.getElementById('loading-screen');
      const hero   = document.querySelector('.hero');
      const topbar = document.querySelector('.topbar');
      if (!loader && !hero) return;

      // LOADER-ONCE: already seen this session → skip the loader AND skip
      // the hero-boxing setup, so the homepage renders straight to its
      // natural layout with no animation.
      let alreadySeen = false;
      try { alreadySeen = sessionStorage.getItem('damlab.loader.seen') === '1'; } catch (_) {}
      if (alreadySeen) {
        if (loader) loader.remove();
        return;
      }

      // Total visible budget: hard cap at 2000ms (loader + dismiss anim).
      const TOTAL_MAX_MS = 2000;
      const ANIM_MS = 500; // dismiss fade duration
      const MIN_MS = Math.max(0, TOTAL_MAX_MS - ANIM_MS); // start dismiss this late at the latest
      const startTime = Date.now();
      let topbarNaturalH = 0;

      if (hero && topbar) {
        topbarNaturalH = topbar.offsetHeight;
        topbar.style.height = '0px';
        topbar.style.overflow = 'hidden';
        topbar.style.opacity = '0';
        hero.style.transition = 'none';
        hero.style.margin = '0';
        hero.style.borderRadius = '0';
        hero.style.height = '100vh';
      }

      let dismissed = false;
      function dismiss() {
        if (!loader || dismissed) return;
        dismissed = true;
        try { sessionStorage.setItem('damlab.loader.seen', '1'); } catch (_) {}
        const elapsed = Date.now() - startTime;
        const delay = Math.max(0, MIN_MS - elapsed);
        setTimeout(() => {
          loader.classList.add('loader--dismiss');
          setTimeout(() => {
            loader.remove();
            setTimeout(() => {
              if (!hero || !topbar) return;
              hero.getBoundingClientRect();
              hero.style.transition = [
                'margin .9s cubic-bezier(.4,0,.2,1)',
                'border-radius .9s cubic-bezier(.4,0,.2,1)',
                'height .9s cubic-bezier(.4,0,.2,1)'
              ].join(', ');
              topbar.style.transition = 'height .6s ease .3s, opacity .6s ease .3s';
              hero.style.margin = '';
              hero.style.borderRadius = '';
              hero.style.height = '';
              topbar.style.height = topbarNaturalH + 'px';
              topbar.style.opacity = '1';
              setTimeout(() => {
                hero.style.transition = '';
                topbar.style.cssText = '';
              }, 1100);
            }, 180);
          }, ANIM_MS);
        }, delay);
      }

      // Hard ceiling — fires even if window.load is slow.
      setTimeout(dismiss, TOTAL_MAX_MS);
      if (document.readyState === 'complete') dismiss();
      else ctx.on(window, 'load', dismiss);
    }
  });

  // ── home.navbar ─────────────────────────────────────
  App.register('home.navbar', {
    mount(ctx) {
      const navbarSticky = document.getElementById('navbarSticky');
      const hero = document.getElementById('hero');
      if (!navbarSticky || !hero) return;

      const obs = new IntersectionObserver(([entry]) => {
        const visible = !entry.isIntersecting;
        navbarSticky.classList.toggle('visible', visible);
        navbarSticky.setAttribute('aria-hidden', String(!visible));
      }, { threshold: 0.05 });
      obs.observe(hero);
      return () => obs.disconnect();
    }
  });

  // home.mobileNav and home.lang are extracted into assets/chrome.js
  // (loaded on every page so the navbar works on product / collection /
  // cart / page templates too).

  // ── home.tabs ───────────────────────────────────────
  // Drives the Populaire keuzes category nav. Supports both the new
  // editorial markup (.producten__nav with sliding indicator) and
  // legacy .tab-btn markup for backwards compatibility.
  App.register('home.tabs', {
    mount(ctx) {
      const nav      = document.querySelector('[data-producten-nav]');
      const tabBtns  = document.querySelectorAll('.producten__nav-btn, .tab-btn');
      const tabPanes = document.querySelectorAll('.product-grid');
      if (!tabBtns.length) return;

      const indicator = nav?.querySelector('[data-producten-indicator]') || null;

      const moveIndicator = (btn) => {
        if (!indicator || !nav) return;
        // Use offsetLeft/offsetWidth (relative to offsetParent = nav)
        // instead of getBoundingClientRect. This is scroll-stable: when
        // .producten__nav is overflow-x: auto on mobile and the user
        // scrolls horizontally, getBoundingClientRect would return
        // viewport-relative positions that drift from the indicator's
        // transform (which is positioned within the nav's content frame).
        indicator.style.width = `${btn.offsetWidth}px`;
        indicator.style.transform = `translateX(${btn.offsetLeft}px)`;
      };

      const activate = (btn, { skipScroll = false } = {}) => {
        const target = btn.dataset.tab;
        tabBtns.forEach(b => {
          const isActive = b === btn;
          b.classList.toggle('is-active', isActive);
          b.classList.toggle('active', isActive);                // legacy class kept for any older CSS
          b.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
        tabPanes.forEach(p => p.classList.toggle('hidden', p.id !== `tab-${target}`));
        moveIndicator(btn);
        if (!skipScroll && nav && btn.scrollIntoView) {
          btn.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
        }
        App.animations?.retriggerReveal(document.getElementById(`tab-${target}`));
      };

      tabBtns.forEach(btn => {
        ctx.on(btn, 'click', () => activate(btn));
        ctx.on(btn, 'keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          const arr = [...tabBtns];
          const i = arr.indexOf(btn);
          const next = arr[(i + (e.key === 'ArrowRight' ? 1 : -1) + arr.length) % arr.length];
          next.focus();
          activate(next);
        });
      });

      // Position the indicator on first paint
      const initial = nav?.querySelector('.producten__nav-btn.is-active') || tabBtns[0];
      requestAnimationFrame(() => {
        moveIndicator(initial);
        nav?.classList.add('is-ready');
      });

      // Reposition on resize (debounced)
      let raf = 0;
      ctx.on(window, 'resize', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const active = nav?.querySelector('.producten__nav-btn.is-active') || tabBtns[0];
          moveIndicator(active);
        });
      });
    }
  });

  // ── home.popularCart ────────────────────────────────
  // Delegates "Toevoegen" clicks inside .producten to App.cart.add().
  // The button carries data-popular-add + data-variant-id (resolved
  // server-side from selected_or_first_available_variant). The cart
  // drawer subscribes to cart:updated and opens itself.
  App.register('home.popularCart', {
    mount(ctx) {
      const root = document.querySelector('.producten');
      if (!root || !App.cart) return;

      ctx.on(root, 'click', async (e) => {
        const btn = e.target.closest('[data-popular-add]');
        if (!btn || btn.disabled) return;
        e.preventDefault();

        const id = parseInt(btn.dataset.variantId, 10);
        if (!Number.isFinite(id) || id <= 0) return;

        const originalLabel = btn.textContent;
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        btn.textContent = 'Toevoegen…';

        try {
          await App.cart.add({ id, quantity: 1 });
          btn.textContent = 'Toegevoegd';
          setTimeout(() => {
            if (ctx.signal?.aborted) return;
            btn.textContent = originalLabel;
            btn.disabled = false;
            btn.removeAttribute('aria-busy');
          }, 1400);
        } catch (err) {
          console.warn('[home.popularCart] add failed', err);
          btn.textContent = 'Probeer opnieuw';
          setTimeout(() => {
            if (ctx.signal?.aborted) return;
            btn.textContent = originalLabel;
            btn.disabled = false;
            btn.removeAttribute('aria-busy');
          }, 1800);
        }
      });
    }
  });

  // ── home.faq ────────────────────────────────────────
  App.register('home.faq', {
    mount(ctx) {
      const btns = document.querySelectorAll('.faq-question');
      if (!btns.length) return;

      function close(btn) {
        const a = btn.nextElementSibling;
        if (a) a.style.height = '0';
        btn.setAttribute('aria-expanded', 'false');
        btn.closest('.faq-item')?.classList.remove('is-open');
      }
      function open(btn) {
        const a = btn.nextElementSibling;
        if (a) a.style.height = a.scrollHeight + 'px';
        btn.setAttribute('aria-expanded', 'true');
        btn.closest('.faq-item')?.classList.add('is-open');
      }

      btns.forEach(btn => {
        ctx.on(btn, 'click', () => {
          const isOpen = btn.getAttribute('aria-expanded') === 'true';
          btns.forEach(o => { if (o !== btn) close(o); });
          isOpen ? close(btn) : open(btn);
        });
      });
    }
  });

  // ── home.hscroll ────────────────────────────────────
  App.register('home.hscroll', {
    mount(ctx) {
      const section = document.getElementById('hscroll');
      const track = document.getElementById('hScrollTrack');
      const bar = document.getElementById('hScrollBar');
      const wrapper = document.getElementById('hScrollWrapper');
      const dotsEl = document.getElementById('hScrollDots');
      if (!section || !track) return;

      function getMax() {
        if (!track) return 0;
        const w = track.parentElement;
        return Math.max(track.scrollWidth - w.offsetWidth, 0);
      }
      function setHeight() {
        if (window.innerWidth < 768) {
          section.style.height = '';
          track.style.transform = '';
          if (bar) bar.style.width = '0%';
          return;
        }
        section.style.height = `calc(100vh + ${getMax()}px)`;
      }
      function update() {
        if (window.innerWidth < 768) return;
        const rect = section.getBoundingClientRect();
        const scrolled = -rect.top;
        const maxScroll = section.offsetHeight - window.innerHeight;
        const progress = Math.min(Math.max(scrolled / maxScroll, 0), 1);
        const max = getMax();
        track.style.transform = `translateX(${-max * progress}px)`;
        if (bar) bar.style.width = `${progress * 100}%`;
      }
      function initDots() {
        if (!dotsEl || !wrapper) return;
        if (window.innerWidth >= 768) return;
        const cards = track.querySelectorAll('.hscroll-card');
        dotsEl.innerHTML = '';
        cards.forEach((_, i) => {
          const d = document.createElement('div');
          d.className = 'hscroll-dot' + (i === 0 ? ' active' : '');
          dotsEl.appendChild(d);
        });
        ctx.on(wrapper, 'scroll', () => {
          const cardW = (cards[0]?.offsetWidth ?? 0) + 16;
          const idx = Math.round(wrapper.scrollLeft / cardW);
          dotsEl.querySelectorAll('.hscroll-dot').forEach((d, i) => d.classList.toggle('active', i === idx));
        }, { passive: true });
      }

      ctx.on(window, 'load', () => { setHeight(); initDots(); });
      setHeight();
      initDots();
      ctx.on(window, 'scroll', update, { passive: true });
      ctx.on(window, 'resize', () => { setHeight(); update(); }, { passive: true });
    }
  });

  // ── home.smoothAnchors ──────────────────────────────
  App.register('home.smoothAnchors', {
    mount(ctx) {
      const navbarSticky = document.getElementById('navbarSticky');

      function smoothTo(targetY, duration = 900) {
        const startY = window.scrollY;
        const diff = targetY - startY;
        let start = null;
        const ease = t => t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t + 2, 3)/2;
        function step(ts) {
          if (!start) start = ts;
          const p = Math.min((ts - start) / duration, 1);
          window.scrollTo(0, startY + diff * ease(p));
          if (p < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      }

      document.querySelectorAll('a[href^="#"]').forEach(a => {
        ctx.on(a, 'click', (e) => {
          const sel = a.getAttribute('href');
          if (!sel || sel === '#') return;
          const target = document.querySelector(sel);
          if (!target) return;
          e.preventDefault();
          const offset = (navbarSticky?.offsetHeight || 0) + 16;
          smoothTo(target.getBoundingClientRect().top + window.scrollY - offset);
        });
      });
    }
  });

  // ── home.marquee ────────────────────────────────────
  App.register('home.marquee', {
    mount(ctx) {
      const marquee = document.querySelector('.social-marquee');
      const track = document.querySelector('.social-marquee__track');
      if (!marquee || !track) return;

      const GAP = 16;
      const SPEED = 0.5;
      let offset = 0;
      let paused = false;
      let rafId = 0;

      ctx.on(marquee, 'mouseenter', () => paused = true);
      ctx.on(marquee, 'mouseleave', () => paused = false);

      function tick() {
        if (!paused) {
          offset += SPEED;
          track.style.transform = `translateX(-${offset}px)`;
          const first = track.firstElementChild;
          if (first) {
            const cw = first.offsetWidth + GAP;
            if (offset >= cw) { track.appendChild(first); offset -= cw; }
          }
        }
        rafId = requestAnimationFrame(tick);
      }
      rafId = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(rafId);
    }
  });

  // ── Generic custom-video-player factory ─────────────
  function videoPlayerModule(name, ids) {
    App.register(name, {
      mount(ctx) {
        const video = document.getElementById(ids.video);
        const card = video?.closest(ids.cardSelector);
        const overlay = document.getElementById(ids.overlay);
        const toggle = ids.toggle ? document.getElementById(ids.toggle) : null;
        const playBtn = ids.playBtn ? document.getElementById(ids.playBtn) : null;
        const track = document.getElementById(ids.track);
        const fill = document.getElementById(ids.fill);
        const thumb = document.getElementById(ids.thumb);
        if (!video || !card) return;

        let scrubbing = false;

        function setProgress(pct) {
          if (fill)  fill.style.width = pct + '%';
          if (thumb) thumb.style.left  = pct + '%';
          track?.setAttribute('aria-valuenow', String(Math.round(pct)));
        }
        function pctFromEvent(e) {
          const rect = track.getBoundingClientRect();
          const x = e.touches ? e.touches[0].clientX : e.clientX;
          return Math.max(0, Math.min(1, (x - rect.left) / rect.width));
        }
        function togglePlay() { video.paused ? video.play() : video.pause(); }
        function syncState() {
          const paused = video.paused;
          card.classList.toggle('is-paused', paused);
          if (overlay) {
            overlay.classList.toggle('is-playing', !paused);
            overlay.classList.toggle('is-paused', paused);
          }
        }

        ctx.on(video, 'timeupdate', () => {
          if (scrubbing || !video.duration) return;
          setProgress((video.currentTime / video.duration) * 100);
        });
        ctx.on(video, 'seeked', () => {
          if (!scrubbing && video.duration) setProgress((video.currentTime / video.duration) * 100);
        });
        ctx.on(video, 'click', togglePlay);
        ctx.on(video, 'play',  syncState);
        ctx.on(video, 'pause', syncState);
        if (overlay) ctx.on(overlay, 'click', togglePlay);
        if (playBtn) ctx.on(playBtn, 'click', e => { e.stopPropagation(); togglePlay(); });
        if (toggle)  ctx.on(toggle,  'click', e => { e.stopPropagation(); togglePlay(); });

        function startScrub(e) { scrubbing = true; track.classList.add('is-scrubbing'); seek(e); }
        function seek(e) {
          const pct = pctFromEvent(e);
          setProgress(pct * 100);
          if (video.duration) video.currentTime = pct * video.duration;
        }
        function endScrub() {
          if (!scrubbing) return;
          scrubbing = false;
          track.classList.remove('is-scrubbing');
        }
        if (track) {
          ctx.on(track, 'mousedown', startScrub);
          ctx.on(document, 'mousemove', e => { if (scrubbing) seek(e); });
          ctx.on(document, 'mouseup',   endScrub);
          ctx.on(track, 'touchstart', startScrub, { passive: true });
          ctx.on(document, 'touchmove', e => { if (scrubbing) seek(e); }, { passive: true });
          ctx.on(document, 'touchend',  endScrub);
        }

        // Optional volume controls
        if (ids.volMute && ids.volSlider) {
          const volMute = document.getElementById(ids.volMute);
          const volSlider = document.getElementById(ids.volSlider);
          if (volMute && volSlider) {
            const updateTrack = (val) => {
              const pct = val * 100;
              volSlider.style.background = `linear-gradient(to right, #00B4D8 ${pct}%, rgba(255,255,255,0.25) ${pct}%)`;
            };
            const syncIcons = () => {
              const muted = video.muted || video.volume === 0;
              const iconVol  = volMute.querySelector('.icon-vol');
              const iconMute = volMute.querySelector('.icon-mute');
              if (iconVol)  iconVol.style.display  = muted ? 'none' : '';
              if (iconMute) iconMute.style.display = muted ? '' : 'none';
            };
            ctx.on(volSlider, 'input', () => {
              video.volume = parseFloat(volSlider.value);
              video.muted = video.volume === 0;
              updateTrack(volSlider.value);
              syncIcons();
            });
            ctx.on(volMute, 'click', () => {
              video.muted = !video.muted;
              if (!video.muted && video.volume === 0) { video.volume = 0.5; volSlider.value = 0.5; }
              volSlider.value = video.muted ? 0 : video.volume;
              updateTrack(volSlider.value);
              syncIcons();
            });
            updateTrack(1);
          }
        }
      }
    });
  }
  videoPlayerModule('home.installVideo', {
    video: 'installVideo', cardSelector: '.install-card',
    overlay: 'installOverlay', playBtn: 'installPlay', toggle: 'installToggle',
    track: 'installTrack', fill: 'installFill', thumb: 'installThumb',
    volMute: 'volMute', volSlider: 'volSlider'
  });
  videoPlayerModule('home.waterVideo', {
    video: 'waterVideo', cardSelector: '#videoCard',
    overlay: 'videoOverlay', toggle: 'videoToggle',
    track: 'videoTrack', fill: 'videoFill', thumb: 'videoThumb'
  });

  // ── home.wfBars ─────────────────────────────────────
  App.register('home.wfBars', {
    mount(ctx) {
      const section = document.querySelector('.wf-showcase');
      const fills = document.querySelectorAll('.wf-bar-fill');
      if (!section || !fills.length) return;

      fills.forEach(f => f.style.setProperty('--pct', f.dataset.pct + '%'));
      App.animations.onceVisible(section, (el) => {
        el.querySelectorAll('.wf-bar-fill').forEach((f, i) => {
          setTimeout(() => f.classList.add('is-animated'), i * 100);
        });
      });
    }
  });

  // ── Route: which modules mount on which page ────────
  // 'index' = homepage. Add more page keys later
  // (e.g. 'product', 'collection', 'cart').
  App.route('index', [
    'home.loader',
    'home.navbar',
    'home.tabs',
    'home.popularCart',
    'home.faq',
    'home.hscroll',
    'home.smoothAnchors',
    'home.marquee',
    'home.installVideo',
    'home.waterVideo',
    'home.wfBars'
  ]);
  // Shared chrome (chrome.mobileNav) is in assets/chrome.js,
  // routed to '*' so it mounts on every template.

})(window.App);
